import { ReactNode, useEffect, useMemo, useState } from "react";
import { ArrowRight, Bell, BookOpenText, CalendarClock, CalendarPlus, CheckCircle2, ClipboardList, Copy, PackageCheck, Phone, RefreshCw, Search, Truck, UserRoundCheck, WalletCards } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import StoreLayout from "./StoreLayout";
import { getCurrentStoreUser, getStoreDashboard, logoutStore, StoreApiError, StoreDashboard as StoreDashboardData, StoreDashboardReservation, StoreDashboardTask, StoreUser } from "./api";
import { copyPhoneNumber } from "./phoneActions";
import { preferredStoreLocationId, rememberedStoreLocationId } from "./storeLocationPreference";

const statusLabels: Record<string, string> = {
  New: "新預約",
  Contacted: "已聯絡",
  Confirmed: "已確認",
  Arrived: "已到店",
  Completed: "已完成",
  NoShow: "未到",
  Cancelled: "已取消",
  FollowUp: "需回訪",
};

const statusClass: Record<string, string> = {
  New: "bg-[#eef3f8] text-[#255071]",
  Contacted: "bg-[#e9f4ec] text-[#245438]",
  Confirmed: "bg-[#e4f0e8] text-[#203d32]",
  Arrived: "bg-[#fff6df] text-[#7a5610]",
  Completed: "bg-[#eeeeee] text-[#59635d]",
  NoShow: "bg-[#fdebea] text-[#9a2d2d]",
  Cancelled: "bg-[#fdebea] text-[#9a2d2d]",
  FollowUp: "bg-[#f3ecdf] text-[#8a6a2f]",
};

function formatDateTime(date: string | null, time: string | null) {
  if (!date && !time) return "未指定時間";
  if (!date) return time || "未指定時間";
  return `${date}${time ? ` ${time}` : ""}`;
}

function MetricCard({ href, icon, label, tone = "default", value }: { href?: string; icon: ReactNode; label: string; tone?: "default" | "warning" | "danger"; value: number }) {
  const toneClass =
    tone === "danger"
      ? "border-[#f2c9c6] bg-[#fdf3f2]"
      : tone === "warning"
        ? "border-[#f0ddb0] bg-[#fff9ed]"
        : "border-[#dfe6e1] bg-white";
  const iconClass = tone === "danger" ? "bg-[#fdebea] text-[#9a2d2d]" : tone === "warning" ? "bg-[#fff1dc] text-[#8a5b14]" : "bg-[#e9f4ec] text-[#245438]";

  const content = (
    <div className="flex min-h-[92px] items-center gap-3">
      <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${iconClass}`}>{icon}</span>
      <div className="min-w-0">
        <p className="text-3xl font-extrabold leading-none text-[#203d32]">{value}</p>
        <p className="mt-2 break-words text-sm font-bold leading-5 text-[#59665f]">{label}</p>
      </div>
    </div>
  );

  if (href) {
    return <Link className={`block min-w-0 rounded-md border p-4 transition hover:-translate-y-0.5 hover:shadow-[0_10px_26px_rgba(32,61,50,.08)] ${toneClass}`} to={href}>{content}</Link>;
  }
  return <div className={`min-w-0 rounded-md border p-4 ${toneClass}`}>{content}</div>;
}

function ReservationCard({ item }: { item: StoreDashboardReservation }) {
  return (
    <div className="rounded-md border border-[#dfe6e1] bg-white p-4 transition hover:border-[#b8c8bf] hover:shadow-[0_8px_22px_rgba(32,61,50,.08)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-base font-extrabold text-[#203d32]">{item.CustomerName}</p>
          <p className="mt-1 text-sm font-medium text-[#59665f]">{formatDateTime(item.AppointmentDate, item.AppointmentTime)}</p>
        </div>
        <span className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${statusClass[item.Status] || "bg-[#eeeeee] text-[#59635d]"}`}>
          {statusLabels[item.Status] || item.Status}
        </span>
      </div>
      <div className="mt-4 rwd-action-grid">
        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-3 text-sm font-bold text-white" onClick={() => copyPhoneNumber(item.CustomerPhone)} type="button">
          <Copy size={16} />
          複製電話
        </button>
        <Link
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]"
          to={`/store/reservations/${encodeURIComponent(item.SourceType)}/${encodeURIComponent(item.SourceId)}`}
        >
          <ClipboardList size={16} />
          詳情
        </Link>
      </div>
    </div>
  );
}

function TaskRow({ item }: { item: StoreDashboardTask }) {
  const toneClass =
    item.Tone === "danger"
      ? "border-[#f2c9c6] bg-[#fff8f7]"
      : item.Tone === "warning"
        ? "border-[#f0ddb0] bg-[#fff9ed]"
        : item.Tone === "success"
          ? "border-[#cfe3d5] bg-[#f2f9f4]"
          : "border-[#dfe6e1] bg-white";
  const countClass = item.Tone === "danger" ? "text-[#9a2d2d]" : item.Tone === "warning" ? "text-[#8a5b14]" : "text-[#203d32]";

  return (
    <Link className={`grid gap-3 rounded-md border p-4 transition hover:border-[#b8c8bf] hover:shadow-[0_8px_22px_rgba(32,61,50,.08)] sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center ${toneClass}`} to={item.Href}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-extrabold text-[#203d32]">{item.Title}</h3>
          {item.Count > 0 && <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-[#59665f]">{item.Count} 筆</span>}
        </div>
        {item.Description && <p className="mt-1 break-words text-sm font-medium leading-6 text-[#59665f]">{item.Description}</p>}
      </div>
      <div className="flex items-center justify-between gap-3 sm:justify-end">
        <span className={`text-2xl font-extrabold ${countClass}`}>{item.Count}</span>
        <span className="inline-flex h-10 min-w-[96px] items-center justify-center gap-2 rounded-md bg-[#315746] px-3 text-sm font-bold text-white">
          處理
          <ArrowRight size={16} />
        </span>
      </div>
    </Link>
  );
}

export default function StoreDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState<StoreUser | null>(null);
  const [dashboard, setDashboard] = useState<StoreDashboardData | null>(null);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(() => rememberedStoreLocationId());
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const effectiveLocationId = useMemo(() => {
    return preferredStoreLocationId(user, currentLocationId);
  }, [currentLocationId, user]);

  async function load(locationId?: string | null) {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentStoreUser();
      setUser(me.User);
      const nextLocationId = preferredStoreLocationId(me.User, locationId || currentLocationId);
      setCurrentLocationId(nextLocationId);
      const data = await getStoreDashboard(nextLocationId);
      setDashboard(data);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "門市工作總覽載入失敗。");
      if (error instanceof StoreApiError && error.status === 401) {
        navigate("/store-login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logoutStore().catch(() => undefined);
    navigate("/store-login", { replace: true });
  }

  function handleLocationChange(locationId: string) {
    setCurrentLocationId(locationId);
    load(locationId);
  }

  const metrics = dashboard?.Metrics || {
    TodayReservations: 0,
    NewReservations: 0,
    TodayFollowUps: 0,
    UpcomingArrivals: 0,
    OverdueFollowUps: 0,
    PickupOrders: 0,
    StoreOrdersInTransit: 0,
    StoreOrdersReadyForPickup: 0,
    BalanceDueOrders: 0,
    NotifiedUnpickedOrders: 0,
    UnreadAnnouncements: 0,
    MyTasks: 0,
    ScheduleCoverageDays: null,
    ScheduleCoverageNeedsAttention: false,
  };

  return (
    <StoreLayout currentLocationId={effectiveLocationId} onLocationChange={handleLocationChange} onLogout={handleLogout} title="門市工作總覽" user={user}>
      {loading && <div className="rounded-md border border-[#dfe6e1] bg-white p-8 text-center text-[#59665f]">載入中...</div>}

      {!loading && message && (
        <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>
      )}

      {!loading && !message && (
        <div className="space-y-5">
          <div className="flex flex-col gap-3 rounded-md border border-[#dfe6e1] bg-white p-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-bold text-[#7b877f]">門市現場工作入口</p>
              <h2 className="mt-1 text-xl font-extrabold text-[#203d32]">先處理今日預約、待聯絡與逾期回訪</h2>
            </div>
            <div className="rwd-action-grid">
              <Link className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 text-sm font-bold text-white" to="/store/customers">
                <Search size={16} />
                客戶搜尋
              </Link>
              <button
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]"
                onClick={() => load(effectiveLocationId)}
                type="button"
              >
                <RefreshCw size={16} />
                重新整理
              </button>
            </div>
          </div>

          <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
            <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-sm font-bold text-[#7b877f]">每日工作總覽</p>
                <h2 className="mt-1 text-xl font-extrabold text-[#203d32]">今日優先處理清單</h2>
              </div>
              <span className="text-sm font-bold text-[#59665f]">共 {dashboard?.Tasks?.reduce((sum, item) => sum + Number(item.Count || 0), 0) || 0} 件待處理</span>
            </div>
            {(dashboard?.Tasks || []).length === 0 ? (
              <div className="flex items-center justify-center gap-2 rounded-md border border-[#cfe3d5] bg-[#f2f9f4] px-4 py-8 text-sm font-bold text-[#245438]">
                <CheckCircle2 size={18} />
                目前沒有待處理工作。
              </div>
            ) : (
              <div className="grid gap-3 xl:grid-cols-2">
                {(dashboard?.Tasks || []).map((item) => <TaskRow item={item} key={item.Key} />)}
              </div>
            )}
          </section>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 min-[1760px]:grid-cols-6">
            <MetricCard href="/store/reservations" icon={<CalendarClock size={18} />} label="今日預約" value={metrics.TodayReservations} />
            <MetricCard href="/store/reservations" icon={<CalendarPlus size={18} />} label="新進待聯絡" tone={metrics.NewReservations > 0 ? "warning" : "default"} value={metrics.NewReservations} />
            <MetricCard href="/store/reservations" icon={<UserRoundCheck size={18} />} label="即將到店" value={metrics.UpcomingArrivals} />
            <MetricCard href="/store/follow-ups?status=Today" icon={<Bell size={18} />} label="今日回訪" tone={metrics.TodayFollowUps > 0 ? "warning" : "default"} value={metrics.TodayFollowUps} />
            <MetricCard href="/store/follow-ups?status=Overdue" icon={<Phone size={18} />} label="逾期回訪" tone={metrics.OverdueFollowUps > 0 ? "danger" : "default"} value={metrics.OverdueFollowUps} />
            <MetricCard href="/store/orders" icon={<PackageCheck size={18} />} label="訂單待處理" tone={metrics.PickupOrders > 0 ? "warning" : "default"} value={metrics.PickupOrders} />
            <MetricCard href="/store/orders?Status=ShippedToStore" icon={<Truck size={18} />} label="已到店待通知" tone={metrics.StoreOrdersInTransit > 0 ? "warning" : "default"} value={metrics.StoreOrdersInTransit} />
            <MetricCard href="/store/orders?Status=ReadyForPickup" icon={<PackageCheck size={18} />} label="可取件/已通知" tone={metrics.StoreOrdersReadyForPickup > 0 ? "warning" : "default"} value={metrics.StoreOrdersReadyForPickup} />
            <MetricCard href="/store/orders?PaymentStatus=BalanceDue" icon={<WalletCards size={18} />} label="剩餘待付金額" tone={metrics.BalanceDueOrders > 0 ? "warning" : "default"} value={metrics.BalanceDueOrders} />
            <MetricCard href="/store/orders?Status=ReadyForPickup" icon={<Bell size={18} />} label="已通知未取" tone={metrics.NotifiedUnpickedOrders > 0 ? "warning" : "default"} value={metrics.NotifiedUnpickedOrders} />
            <MetricCard href="/store/announcements?status=Unread" icon={<BookOpenText size={18} />} label="未讀公告" tone={metrics.UnreadAnnouncements > 0 ? "warning" : "default"} value={metrics.UnreadAnnouncements} />
          </div>

          {metrics.ScheduleCoverageNeedsAttention && (
            <Link className="flex flex-col gap-3 rounded-md border border-[#f0ddb0] bg-[#fff9ed] p-4 transition hover:shadow-[0_10px_26px_rgba(32,61,50,.08)] md:flex-row md:items-center md:justify-between" to="/store/schedule">
              <div>
                <p className="text-sm font-bold text-[#8a5b14]">門市預約時段需要維護</p>
                <h3 className="mt-1 text-lg font-extrabold text-[#203d32]">
                  官網可預約日期{metrics.ScheduleCoverageDays === null || metrics.ScheduleCoverageDays === undefined ? "尚未建立" : `約剩 ${metrics.ScheduleCoverageDays} 天`}
                </h3>
              </div>
              <span className="inline-flex h-10 items-center justify-center rounded-md bg-[#315746] px-4 text-sm font-bold text-white">前往設定</span>
            </Link>
          )}

          <div className="grid gap-5 2xl:grid-cols-[1.2fr_.8fr]">
            <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-extrabold text-[#203d32]">今日預約</h3>
                  <p className="mt-1 text-sm text-[#7b877f]">建議依到店時間由上往下處理。</p>
                </div>
                <Link className="text-sm font-bold text-[#315746] hover:underline" to="/store/reservations">
                  查看全部
                </Link>
              </div>
              {(dashboard?.TodayReservations || []).length === 0 ? (
                <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-8 text-center text-sm font-medium text-[#7b877f]">
                  目前沒有今日預約。
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {dashboard?.TodayReservations.map((item) => <ReservationCard item={item} key={`${item.SourceType}-${item.SourceId}`} />)}
                </div>
              )}
            </section>

            <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
              <div className="mb-4">
                <h3 className="text-base font-extrabold text-[#203d32]">待處理重點</h3>
                <p className="mt-1 text-sm text-[#7b877f]">新預約與逾期回訪會優先出現在這裡。</p>
              </div>

              <div className="space-y-3">
                {(dashboard?.NewReservations || []).slice(0, 4).map((item) => (
                  <ReservationCard item={item} key={`new-${item.SourceType}-${item.SourceId}`} />
                ))}

                {(dashboard?.FollowUps || []).slice(0, 4).map((item) => (
                  <div className="rounded-md border border-[#f0ddb0] bg-[#fff9ed] p-4" key={item.FollowUpId}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-extrabold text-[#203d32]">{item.CustomerName}</p>
                        <p className="mt-1 line-clamp-2 text-sm text-[#7a5610]">{item.Subject}</p>
                        <p className="mt-2 text-xs font-bold text-[#7b877f]">回訪時間：{item.NextFollowUpAt || "-"}</p>
                      </div>
                      <span className="inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-bold text-[#8a6a2f]">回訪</span>
                    </div>
                    <div className="rwd-action-grid">
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-3 text-sm font-bold text-white" onClick={() => copyPhoneNumber(item.CustomerPhone)} type="button">
                        <Copy size={16} />
                        複製電話
                      </button>
                      <Link className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" to="/store/follow-ups?status=Overdue">
                        <ClipboardList size={16} />
                        處理
                      </Link>
                    </div>
                  </div>
                ))}

                {(dashboard?.NewReservations || []).length === 0 && (dashboard?.FollowUps || []).length === 0 && (
                  <div className="flex items-center justify-center gap-2 rounded-md border border-[#cfe3d5] bg-[#f2f9f4] px-4 py-8 text-sm font-bold text-[#245438]">
                    <CheckCircle2 size={18} />
                    目前沒有急件。
                  </div>
                )}
              </div>
            </section>
          </div>

          {(dashboard?.ArrivalOrders || []).length > 0 && (
            <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-extrabold text-[#203d32]">到店通知與取件</h3>
                  <p className="mt-1 text-sm text-[#7b877f]">商品到店後，門市優先處理通知、未取追蹤與交付。</p>
                </div>
                <Link className="text-sm font-bold text-[#315746] hover:underline" to="/store/orders?Status=ReadyForPickup">
                  查看訂單
                </Link>
              </div>
              <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                {dashboard?.ArrivalOrders?.map((item) => (
                  <Link className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4 transition hover:border-[#b8c8bf] hover:bg-white" key={item.StoreSalesOrderId} to={`/store/orders/${encodeURIComponent(item.StoreSalesOrderId)}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="break-all text-sm font-bold text-[#7b877f]">{item.OrderNumber}</p>
                        <p className="mt-1 truncate text-base font-extrabold text-[#203d32]">{item.CustomerName}</p>
                        <p className="mt-1 text-sm font-bold text-[#59665f]">{item.CustomerPhone}</p>
                      </div>
                      <span className="shrink-0 rounded-full bg-[#e9f4ec] px-2.5 py-1 text-xs font-bold text-[#245438]">{item.StatusLabel || item.Status}</span>
                    </div>
                    <p className="mt-3 text-xs font-bold text-[#7b877f]">預計取件：{item.ExpectedPickupDate || "-"}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </StoreLayout>
  );
}
